/* Charms bracelet builder (sections/charms-bracelet-builder.liquid)

   Products come page by page from sections/charms-bracelet-feed.liquid through the Section
   Rendering API, so every price, currency and link is rendered by Shopify for the shopper's
   own market. The cart is Shopify's Ajax cart; every change asks for the same feed section
   rendered on /cart, so the page always works from the real cart (line keys, money-formatted
   totals, tags for the free-bracelet count) without formatting money in JavaScript.

   After each change the theme gets `theme:cart:refresh`, which updates the header count
   without opening the theme's drawer or its "item added" bar (both listen to
   `theme:product:added`, which this file never sends). */
(function () {
  'use strict';

  var root = document.querySelector('[data-cbb]');
  if (!root || root.dataset.cbbReady) return;
  root.dataset.cbbReady = '1';

  var CFG = JSON.parse(root.querySelector('[data-cbb-config]').textContent);
  var T = CFG.t;
  var G = CFG.gift || {};
  var THRESHOLD = Math.max(1, parseInt(G.threshold, 10) || 10);
  /* The ladder's three steps. Counting runs to the last one, so a cart of thirteen
     reads 13/25 instead of sticking at 10/10 the moment the bracelet is won. */
  var TIERS = CFG.tiers || {};
  /* Free shipping is the first step, from SHIP_AT charms, or from SHIP_TOTAL in the
     shop's currency (see shipFreeByTotal). It adds nothing to the cart. */
  var SHIP_AT = parseInt(TIERS.shipping, 10) || 0;
  var SHIP_TOTAL = parseFloat(TIERS.shippingTotal) || 0;
  var STEPS = [
    { key: 'shipping', at: SHIP_AT },
    { key: 'bracelet', at: THRESHOLD },
    { key: 'mystery', at: parseInt(TIERS.mystery, 10) || 0 },
    { key: 'watch', at: parseInt(TIERS.watch, 10) || 0 }
  ].filter(function (s) { return s.at > 0; })
    .sort(function (a, b) { return a.at - b.at; });
  var LADDER = STEPS.map(function (s) { return s.at; });
  var TIER_KEYS = STEPS.map(function (s) { return s.key; });
  var TOP = LADDER[LADDER.length - 1] || THRESHOLD;
  var FEED = CFG.feed;
  var ROOT_URL = String(CFG.routes.root || '/').replace(/\/?$/, '/');
  var jsUrl = function (u) { return /\.js$/.test(u) ? u : u + '.js'; };
  var ADD_URL = jsUrl(CFG.routes.add);
  var CHANGE_URL = jsUrl(CFG.routes.change);
  var UPDATE_URL = jsUrl(CFG.routes.update || (ROOT_URL + 'cart/update'));
  var CART_URL = CFG.routes.cart;
  var LINK_RATIO = 0.86; // a charm link is 0.86 times as tall as it is wide
  var FIRST_EAGER = 3;   // shelves fetched straight away; the rest load as they come near

  var $ = function (sel, ctx) { return (ctx || root).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || root).querySelectorAll(sel)); };
  var esc = function (v) {
    return String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  };
  var fill = function (str, map) {
    map = map || {};
    if (!('customers' in map)) map.customers = CFG.customers || '';
    return String(str || '').replace(/\[(\w+)\]/g, function (m, k) { return Object.prototype.hasOwnProperty.call(map, k) ? map[k] : m; });
  };
  var lower = function (list) {
    return (list || []).map(function (x) { return String(x).trim().toLowerCase(); }).filter(Boolean);
  };

  if (root.hasAttribute('data-cbb-hide-notification')) document.body.classList.add('cbb-page');
  root.style.setProperty('--cbb-slots', THRESHOLD);

  var BAG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 9h14l-1.2 11H6.2L5 9z"/><path d="M9 9V7a3 3 0 016 0v2"/><path d="M12 12.5v5M9.5 15h5"/></svg>';
  var BACK = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>';
  var SKEL = '<div class="cbb-skel" aria-hidden="true"><i></i><b></b><b></b><em></em></div>';

  /* ---------------------------------------------------------------- products */
  var SILVER = lower(CFG.metal && CFG.metal.silver);
  var GOLD = lower(CFG.metal && CFG.metal.gold);
  var HANG = String(CFG.hangTag || '').toLowerCase();
  var LOW = String(CFG.lowStock || '').toLowerCase();
  var HIDE = String(CFG.hideTag || '').toLowerCase(); // mystery boxes and the like never belong in a charm shelf
  var products = new Map();

  // Gold and Silver show only those two finishes. Rose gold and black are finishes in
  // their own right and appear under All alone, so metalOf returns '' for them and
  // passMetal then lets them through on 'all' only.
  //
  // Rose gold is caught before the tag checks on purpose: the word "gold" sits inside it,
  // and a few rose gold charms also carry the plain gold tag, so anything later would
  // claim them. Black is caught after, because a silver charm with black enamel
  // ("Black Flame, on Silver") is still silver and has to stay under Silver. Only a charm
  // carrying no silver or gold of its own counts as a black finish.
  function metalOf(tagsLower, title) {
    var t = String(title || '').toLowerCase();
    if (/rose\s*gold/.test(t)) return '';
    if (tagsLower.some(function (x) { return /rose\s*gold/.test(x); })) return '';
    if (tagsLower.some(function (x) { return SILVER.indexOf(x) > -1; })) return 'silver';
    if (tagsLower.some(function (x) { return GOLD.indexOf(x) > -1; })) return 'gold';
    if (tagsLower.indexOf('black') > -1) return '';
    if (/silver/.test(t)) return 'silver';
    if (/gold/.test(t)) return 'gold';
    return '';
  }

  function prep(p) {
    if (products.has(p.id)) return products.get(p.id);
    var tags = lower(p.tags);
    p.metal = metalOf(tags, p.t);
    p.hang = !!HANG && tags.indexOf(HANG) > -1;
    p.charm = tags.indexOf(G.tag) > -1;
    p.skip = !!HIDE && tags.indexOf(HIDE) > -1;
    p.low = LOW ? ((p.tags || []).filter(function (t) { return String(t).toLowerCase().indexOf(LOW) > -1; })[0] || '') : '';
    p.off = p.c > p.p ? Math.round((1 - p.p / p.c) * 100) : 0;
    p.ar = p.ar > 0 ? p.ar : 1;
    products.set(p.id, p);
    return p;
  }

  /* -------------------------------------------------------------------- feed */
  var feedCache = new Map();

  function parseFeed(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var el = doc.querySelector('[data-cbb-feed]');
    return el ? JSON.parse(el.textContent) : { type: 'none' };
  }

  function getFeed(base, page, sort) {
    var u = new URL(base, location.origin);
    u.searchParams.set('section_id', FEED);
    u.searchParams.set('page', String(page));
    if (sort) u.searchParams.set('sort_by', sort);
    var key = u.pathname + u.search;
    if (!feedCache.has(key)) {
      var req = fetch(key, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
        .then(function (html) { var d = parseFeed(html); d.items = (d.items || []).map(prep); return d; });
      req.catch(function () { feedCache.delete(key); });
      feedCache.set(key, req);
    }
    return feedCache.get(key);
  }

  /* -------------------------------------------------------------------- cart */
  var cart = { items: [], count: 0, totalf: '' };
  var chain = Promise.resolve();
  var quietUntil = 0; // theme cart events caused by this page are ignored until then

  function setCart(d) {
    if (!d || d.type !== 'cart') return false;
    d.items.forEach(function (i) { i.tagsL = lower(i.tags); });
    cart = d;
    return true;
  }

  /* The cart total reaches free shipping on its own too, the same rule the checkout
     applies. The total is in the shopper's currency, so the line is converted at the
     storefront's rate. */
  function shipFreeByTotal() {
    if (!SHIP_TOTAL || !cart || !cart.total) return false;
    var rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
    return cart.total >= Math.round(SHIP_TOTAL * 100 * rate);
  }

  function loadCart() {
    return fetch(CART_URL + '?section_id=' + encodeURIComponent(FEED), { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) { setCart(parseFeed(html)); });
  }

  function cartPost(url, payload) {
    var body = Object.assign({ sections: FEED, sections_url: CART_URL }, payload);
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().catch(function () { return null; }).then(function (data) {
        if (!res.ok || !data || data.status) {
          var err = new Error((data && (data.description || data.message)) || 'HTTP ' + res.status);
          err.friendly = !!(data && data.description);
          throw err;
        }
        var html = data.sections && data.sections[FEED];
        if (html && setCart(parseFeed(html))) return data;
        return loadCart().then(function () { return data; });
      });
    });
  }

  function addVariant(id, quantity, properties) {
    var item = { id: id, quantity: quantity || 1 };
    if (properties) item.properties = properties;
    return cartPost(ADD_URL, { items: [item] });
  }
  function setLine(key, quantity) { return cartPost(CHANGE_URL, { id: key, quantity: Math.max(0, quantity) }); }

  // Every cart change runs one after another, so quick taps never race each other.
  // A cart change takes a moment, so the dock shows a spinner and stops taking taps until it lands.
  var busy = 0;
  function setBusy(on) {
    busy = Math.max(0, busy + (on ? 1 : -1));
    if (dock) dock.classList.toggle('is-busy', busy > 0);
  }

  function run(task) {
    setBusy(true);
    var p = chain.then(task).catch(function (err) {
      console.warn('[Charms bracelet builder]', err);
      toast(err && err.friendly ? err.message : T.error);
      return loadCart().then(function () { renderAll(false); }).catch(function () {});
    }).then(function (v) { setBusy(false); return v; });
    chain = p;
    return p;
  }

  function tellTheme() {
    quietUntil = Date.now() + 2000;
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
  }

  /* ------------------------------------------------------------ free bracelet */
  var isGift = function (i) { return !!i.gift && (!G.pid || i.pid === G.pid); };
  /* Every reward line carries _free_gift, so none of them counts towards the charm
     total. The property's value says which reward it is, so the bracelet's own
     housekeeping can never reach in and delete the mystery box in its place. */
  var kindOf = function (i) { return String(i.gk || '').toLowerCase(); };
  var isReward = function (i) { return !!i.gift; };
  // the bracelet's own property value is "yes", so anything that is not one of the
  // other two rewards is the bracelet
  var rewardKey = function (i) {
    var k = kindOf(i);
    return k === 'mystery' || k === 'watch' ? k : 'bracelet';
  };
  // rewards the shopper has taken out of the cart on purpose, so nothing re-adds them.
  // The mystery box choice is shared with the theme cart (assets/free-gift.js) for the
  // whole visit, so a box taken out on any page stays out here too.
  var DECLINED_KEY = 'charmsMysteryDeclined';
  var declined = {};
  try { declined.mystery = window.sessionStorage.getItem(DECLINED_KEY) === '1'; } catch (e) {}
  function rememberDeclined(kind, on) {
    declined[kind] = on;
    if (kind !== 'mystery') return;
    try {
      if (on) window.sessionStorage.setItem(DECLINED_KEY, '1');
      else window.sessionStorage.removeItem(DECLINED_KEY);
    } catch (e) {}
  }
  var rewardLines = function (kind) {
    return cart.items.filter(function (i) { return isReward(i) && kindOf(i) === kind; });
  };
  var giftLines = function () {
    return cart.items.filter(function (i) { return isGift(i) && kindOf(i) !== 'mystery' && kindOf(i) !== 'watch'; });
  };
  var qualifies = function (i) { return !i.gift && i.tagsL.indexOf(G.tag) > -1 && (!HIDE || i.tagsL.indexOf(HIDE) < 0); };
  var wasUnlocked = null;

  // Charms in the order they were added (the cart lists the newest first).
  function unitsList() {
    var u = [];
    cart.items.slice().reverse().forEach(function (i) {
      if (qualifies(i)) for (var k = 0; k < i.q; k++) u.push(i);
    });
    return u;
  }

  function addGift(vid) { return addVariant(vid || G.vid, 1, { _free_gift: 'yes' }); }

  // Keeps exactly one free bracelet in the cart while the threshold is met and none below it.
  // Returns true when it changed the cart.
  function syncGift(opts) {
    opts = opts || {};
    if (!G.on || !G.vid) return Promise.resolve(false);
    var n = unitsList().length;
    var lines = giftLines();
    var changed = false;
    var step = Promise.resolve();

    if (n < THRESHOLD) {
      lines.forEach(function (l) { step = step.then(function () { changed = true; return setLine(l.key, 0); }); });
      declined.bracelet = false;
      wasUnlocked = false;
      return step.then(function () { return changed; });
    }

    if (lines.length) {
      if (lines[0].q > 1) step = step.then(function () { changed = true; return setLine(lines[0].key, 1); });
      lines.slice(1).forEach(function (l) { step = step.then(function () { changed = true; return setLine(l.key, 0); }); });
    } else if (G.mode === 'auto') {
      if (!declined.bracelet) step = step.then(function () { changed = true; return addGift(G.vid); });
    } else if (opts.userAction && wasUnlocked === false) {
      openGift(); // the shopper just reached the threshold on this page: ask for the colour
    }
    wasUnlocked = true;
    return step.then(function () { return changed; });
  }

  /* The mystery box has no variants to choose between, so it simply appears once the
     count is reached and goes again if the cart falls back below it. */
  function syncMystery() {
    var vid = parseInt(TIERS.mysteryVid, 10) || 0;
    var at = parseInt(TIERS.mystery, 10) || 0;
    if (!vid || !at) return Promise.resolve(false);
    var n = unitsList().length;
    var lines = rewardLines('mystery');
    var step = Promise.resolve();
    var changed = false;
    if (n < at) {
      lines.forEach(function (l) { step = step.then(function () { changed = true; return setLine(l.key, 0); }); });
      rememberDeclined('mystery', false); // below the step again, so it is offered afresh next time
    } else if (!lines.length) {
      if (!declined.mystery) step = step.then(function () { changed = true; return addVariant(vid, 1, { _free_gift: 'mystery' }); });
    } else if (lines[0].q > 1) {
      step = step.then(function () { changed = true; return setLine(lines[0].key, 1); });
    }
    return step.then(function () { return changed; });
  }

  function afterChange(opts) {
    opts = opts || {};
    return syncGift(opts).then(function () {
      return syncMystery();
    }).then(function () {
      return syncWatch();
    }).then(function () {
      renderAll(!!opts.pulse);
      tellTheme();
      askWatch(opts);
    });
  }

  function goCheckout(btn) {
    // a reward earned and not chosen yet is chosen first: the popup opens and checkout waits
    if (braceletDue()) { openGift(); return; }
    if (watchDue()) { openWatch(); return; }
    if (btn) btn.setAttribute('aria-busy', 'true');
    run(function () {
      // the cart is put right first, from a fresh copy (another tab may have changed it): one
      // of each reward and none without its charms, so checkout never has to turn it away
      var tidy = typeof window.__freeGiftTidy === 'function' ? window.__freeGiftTidy() : Promise.resolve(false);
      return Promise.resolve(tidy).catch(function () { return false; })
        .then(loadCart)
        .then(function () { return syncGift({}); })
        .then(function () { return syncMystery(); })
        .then(function () { return syncWatch(); })
        .then(function () {
          renderAll(false);
          if (braceletDue() || watchDue()) {
            if (btn) btn.removeAttribute('aria-busy');
            if (braceletDue()) openGift(); else openWatch();
            return;
          }
          var step = Promise.resolve();
          // when the bracelet is added by itself, make sure it made it in
          if (G.on && G.vid && G.mode === 'auto' && unitsList().length >= THRESHOLD && !giftLines().length) step = addGift(G.vid);
          return step.then(function () { location.href = ROOT_URL + 'checkout'; });
        });
    });
  }

  /* ------------------------------------------------------------------- cards */
  var metal = 'all';
  var passMetal = function (p) { return metal === 'all' || p.metal === metal; };
  var qtyOf = function (vid) {
    return cart.items.reduce(function (n, i) { return n + (i.v === vid && !i.gift ? i.q : 0); }, 0);
  };

  function badges(p) {
    var b = [];
    if (p.off) b.push('<span class="cbb-bdg cbb-bdg--off">-' + p.off + '%</span>');
    if (p.low) b.push('<span class="cbb-bdg cbb-bdg--warn">' + esc(p.low) + '</span>');
    return b.length ? '<div class="cbb-badges">' + b.join('') + '</div>' : '';
  }

  function stepperHtml(id, q, prefix) {
    return '<div class="cbb-step"><button type="button" data-cbb-' + prefix + 'dec="' + id + '" aria-label="' + esc(T.removeOne) + '">&minus;</button>' +
      '<span data-cbb-q>' + esc(fill(T.inCart, { qty: q })) + '</span>' +
      '<button type="button" data-cbb-' + prefix + 'inc="' + id + '" aria-label="' + esc(T.addOne) + '">+</button></div>';
  }

  function cardHtml(p) {
    var q = qtyOf(p.v);
    var action = !p.av
      ? '<button type="button" class="cbb-atc" disabled>' + esc(T.soldOut) + '</button>'
      : '<button type="button" class="cbb-atc" data-cbb-add="' + p.id + '">' + BAG + esc(T.add) + '</button>' + stepperHtml(p.id, q, '');
    return '<article class="cbb-card' + (p.av ? '' : ' is-sold') + (q ? ' is-in' : '') + '" data-cbb-card="' + p.id + '">' +
      '<button type="button" class="cbb-media" data-cbb-qv="' + p.id + '" aria-label="' + esc(fill(T.quickView, { title: p.t })) + '">' +
        badges(p) + (p.img ? '<img src="' + esc(p.img) + '" alt="' + esc(p.t) + '" loading="lazy">' : '') +
      '</button>' +
      '<button type="button" class="cbb-title" data-cbb-qv="' + p.id + '"><span>' + esc(p.t) + '</span></button>' +
      '<div class="cbb-price">' + esc(p.pf) + (p.c ? '<s>' + esc(p.cf) + '</s>' : '') + '</div>' +
      action + '</article>';
  }

  // The watches tile is rendered by Liquid and each card carries its own variant id,
  // so it needs no feed data: only the "x in cart" line is kept in step with the cart.
  function syncWatches() {
    $$('[data-cbb-wcard]').forEach(function (c) {
      var q = qtyOf(+c.dataset.cbbWcard);
      c.classList.toggle('is-in', q > 0);
      var s = c.querySelector('[data-cbb-wq]');
      if (s) s.textContent = q ? fill(T.inCart, { qty: q }) : '';
    });
  }

  function addWatch(btn, variantId) {
    var label = btn.dataset.cbbLabel || btn.textContent.trim() || T.add;
    btn.dataset.cbbLabel = label;
    btn.classList.add('is-done');
    btn.textContent = '✓ ' + T.added;
    run(function () {
      return addVariant(variantId, 1).then(function () { return afterChange({ pulse: true }); });
    }).then(function () {
      btn.classList.remove('is-done');
      btn.textContent = label;
    });
  }

  function syncCards() {
    $$('[data-cbb-card]').forEach(function (c) {
      var p = products.get(+c.dataset.cbbCard);
      if (!p) return;
      var q = qtyOf(p.v);
      c.classList.toggle('is-in', q > 0);
      var s = c.querySelector('[data-cbb-q]');
      if (s) s.textContent = fill(T.inCart, { qty: q });
    });
  }

  /* ----------------------------------------------------------------- shelves */
  var shelves = (CFG.shelves || []).map(function (s) {
    var el = root.querySelector('[data-cbb-shelf="' + s.id + '"]');
    if (!el) return null;
    return Object.assign({}, s, {
      el: el,
      row: el.querySelector('[data-cbb-row]'),
      wrap: el.querySelector('.cbb-rowwrap'),
      bar: el.querySelector('.cbb-rbar i'),
      // a shelf of two or three rows reads that many times as many charms
      rows: Math.max(1, Math.min(3, parseInt(s.rows, 10) || 1)),
      page: 0, pages: 1, items: [], ids: new Set(), loading: false, started: false, failed: false
    });
  }).filter(Boolean);
  var shelfById = new Map(shelves.map(function (s) { return [s.id, s]; }));

  /* An open shelf has its own link: ?shelf= with the handle of the shelf's collection
     (the block id works too), so a shelf can be linked to from an ad or an email and
     the page opens with that shelf already open. Search is not a shelf and gets none. */
  var SHELF_PARAM = 'shelf';

  function shelfKey(sh) {
    var m = String(sh.url || '').match(/\/collections\/([^\/?#]+)/);
    if (!m) return sh.id;
    try { return decodeURIComponent(m[1]); } catch (e) { return m[1]; }
  }

  function shelfFromKey(key) {
    if (!key) return null;
    key = String(key).toLowerCase();
    for (var i = 0; i < shelves.length; i++) {
      if (shelfKey(shelves[i]).toLowerCase() === key || shelves[i].id.toLowerCase() === key) return shelves[i];
    }
    return null;
  }

  // this page's address with the shelf's link, or without one; other parameters (UTM) stay
  function shelfHref(sh) {
    try {
      var u = new URL(location.href);
      if (sh && shelves.indexOf(sh) >= 0) u.searchParams.set(SHELF_PARAM, shelfKey(sh));
      else u.searchParams.delete(SHELF_PARAM);
      return u.pathname + u.search + u.hash;
    } catch (e) {
      return null;
    }
  }

  function updRow(sh) {
    var row = sh.row;
    var max = row.scrollWidth - row.clientWidth;
    var frac = row.scrollWidth ? row.clientWidth / row.scrollWidth : 1;
    if (sh.bar) {
      sh.bar.style.width = Math.max(18, Math.min(100, frac * 100)) + '%';
      var f = Math.max(0.18, frac);
      sh.bar.style.transform = 'translateX(' + (max > 0 ? (row.scrollLeft / max) * (100 / f - 100) : 0) + '%)';
    }
    sh.wrap.classList.toggle('is-start', row.scrollLeft < 8);
    sh.wrap.classList.toggle('is-end', row.scrollLeft > max - 8);
  }

  function settleShelf(sh) {
    sh.row.querySelectorAll('.cbb-skel').forEach(function (x) { x.remove(); });
    var any = !!sh.row.querySelector('.cbb-card');
    sh.el.hidden = !any && (sh.page >= sh.pages || sh.failed);
    updRow(sh);
  }

  function fillShelf(sh, want) {
    if (sh.loading) return Promise.resolve();
    want = (want || 8) * (sh.rows || 1);
    sh.loading = true;
    sh.started = true;
    if (sh.row.querySelector('.cbb-card')) sh.row.insertAdjacentHTML('beforeend', SKEL + SKEL);
    var added = 0;
    var next = function () {
      if (added >= want || sh.page >= sh.pages) return Promise.resolve();
      return getFeed(sh.url, sh.page + 1, sh.sort).then(function (d) {
        sh.page = d.page || sh.page + 1;
        sh.pages = d.pages || sh.page;
        var fresh = (d.items || []).filter(function (p) { return p.av && !p.skip && !sh.ids.has(p.id); });
        fresh.forEach(function (p) { sh.ids.add(p.id); sh.items.push(p); });
        var vis = fresh.filter(passMetal);
        if (vis.length) {
          sh.row.querySelectorAll('.cbb-skel').forEach(function (x) { x.remove(); });
          sh.row.insertAdjacentHTML('beforeend', vis.map(cardHtml).join(''));
          added += vis.length;
        }
        return next();
      });
    };
    return next().catch(function (e) {
      sh.failed = true;
      console.warn('[Charms bracelet builder] shelf', sh.title, e);
    }).then(function () {
      sh.loading = false;
      settleShelf(sh);
    });
  }

  function rerenderShelves() {
    shelves.forEach(function (sh) {
      if (!sh.started) return;
      var vis = sh.items.filter(passMetal);
      sh.row.innerHTML = vis.map(cardHtml).join('');
      sh.row.scrollLeft = 0;
      sh.el.hidden = false;
      if (vis.length < 6 * (sh.rows || 1) && sh.page < sh.pages) fillShelf(sh, 8 - Math.ceil(vis.length / (sh.rows || 1)));
      else settleShelf(sh);
    });
  }

  shelves.forEach(function (sh) {
    sh.row.addEventListener('scroll', function () {
      updRow(sh);
      if (sh.row.scrollLeft + sh.row.clientWidth > sh.row.scrollWidth - sh.row.clientWidth * 0.7) fillShelf(sh, 10);
    }, { passive: true });
  });

  var shelfObserver = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      shelfObserver.unobserve(e.target);
      var sh = shelves.filter(function (s) { return s.el === e.target; })[0];
      if (sh && !sh.started) fillShelf(sh);
    });
  }, { rootMargin: '700px 0px' }) : null;

  shelves.forEach(function (sh, i) {
    if (i < FIRST_EAGER || !shelfObserver) fillShelf(sh);
    else shelfObserver.observe(sh.el);
  });

  // An app popup can lock page scrolling (the scratch card one does), and then the observer
  // above never fires and every row below stays empty. So the rest of the shelves also fill
  // themselves in the background, one at a time, whether the shopper scrolls or not.
  var queued = [];
  var queueBusy = false;

  function runQueue() {
    if (queueBusy) return;
    var sh = null;
    while (queued.length && !sh) {
      var candidate = queued.shift();
      if (!candidate.started) sh = candidate;
    }
    if (!sh) return;
    queueBusy = true;
    fillShelf(sh).then(function () {
      queueBusy = false;
      setTimeout(runQueue, 400);
    });
  }

  setTimeout(function () {
    shelves.forEach(function (sh) { if (!sh.started) queued.push(sh); });
    runQueue();
  }, 2500);

  /* -------------------------------------------------------------- theme grid */
  var overview = $('[data-cbb-overview]');
  var gridBox = $('[data-cbb-grid]');
  var browse = $('[data-cbb-browse]');
  var sortSel = $('[data-cbb-sort]');
  var view = null;
  var overviewY = 0;

  function headerOffset() {
    var raw = getComputedStyle(document.body).getPropertyValue('--header-sticky-height') ||
      getComputedStyle(document.documentElement).getPropertyValue('--header-sticky-height');
    var v = parseFloat(raw);
    return isNaN(v) ? 0 : v;
  }

  function markTheme(id) {
    $$('[data-cbb-theme]').forEach(function (b) { b.classList.toggle('is-on', b.dataset.cbbTheme === id); });
  }

  function keepHtml(id) {
    var cats = $$('[data-cbb-cats] .cbb-cat').filter(function (c) {
      return c.dataset.cbbTheme !== id && c.dataset.cbbTheme !== 'all';
    }).slice(0, 6).map(function (c) {
      var k = c.cloneNode(true);
      k.classList.remove('is-on');
      k.removeAttribute('data-shopify-editor-block');
      return k.outerHTML;
    }).join('');
    return '<div class="cbb-keep"><h3>' + esc(T.keep) + '</h3>' + (cats ? '<div class="cbb-cats">' + cats + '</div>' : '') +
      '<button type="button" class="cbb-back" data-cbb-back>' + BACK + esc(T.back) + '</button></div>';
  }

  function openTheme(id, opts) {
    opts = opts || {};
    var sh = shelfById.get(id);
    if (!sh) return;
    closeOverlays();
    if (!view) overviewY = scrollY;
    view = { sh: sh, page: 0, pages: 1, total: 0, dropped: 0, items: [], ids: new Set(), sort: (sortSel && sortSel.value) || sh.sort || '', loading: false, limit: GRID_FIRST };
    markTheme(id);
    overview.hidden = true;
    gridBox.hidden = false;
    gridBox.innerHTML =
      '<div class="cbb-backbar"><button type="button" class="cbb-back" data-cbb-back>' + BACK + esc(T.back) + '</button>' +
      '<h2><span>' + esc(sh.title) + '</span></h2><span class="cbb-back cbb-back--ghost" aria-hidden="true">' + BACK + esc(T.back) + '</span></div>' +
      '<div class="cbb-grid" data-cbb-grid-list>' + SKEL + SKEL + SKEL + SKEL + '</div><div data-cbb-grid-more></div>' + keepHtml(id);
    if (!opts.fromHistory) { try { history.pushState({ cbbTheme: id }, '', shelfHref(sh)); } catch (e) { /* history unavailable */ } }
    if (!opts.keepScroll) scrollTo({ top: browse.getBoundingClientRect().top + scrollY - headerOffset() - 12, behavior: 'smooth' });
    return loadGrid();
  }

  /* The grid shows GRID_FIRST charms, then GRID_STEP more each time the shopper scrolls near
     its end; there is no button. view.limit is how many cards should be on screen. The feed
     is read a page at a time until the current metal has that many, and what a page brings
     beyond the limit waits in view.items for the next step. */
  var GRID_FIRST = 20, GRID_STEP = 30;

  function gridShown(list) { return list.querySelectorAll('[data-cbb-card]').length; }

  function gridHasMore(list) {
    return !!view && (view.items.filter(passMetal).length > gridShown(list) || view.page < view.pages);
  }

  // puts the cards up to the limit on screen, after the ones already there
  function gridDeal(list) {
    var shown = gridShown(list);
    var add = view.items.filter(passMetal).slice(shown, view.limit);
    if (!add.length) return;
    list.querySelectorAll('.cbb-skel').forEach(function (x) { x.remove(); });
    list.insertAdjacentHTML('beforeend', add.map(cardHtml).join(''));
  }

  // the footer is watched instead of clicked: coming within 600px of it deals the next step
  var gridEnd = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) { if (e.isIntersecting) gridMore(); });
  }, { rootMargin: '0px 0px 600px 0px' }) : null;

  function gridMore() {
    var list = $('[data-cbb-grid-list]', gridBox);
    if (!view || view.loading || !list || !gridHasMore(list)) return;
    view.limit = gridShown(list) + GRID_STEP;
    loadGrid();
  }

  function gridFooter() {
    var box = $('[data-cbb-grid-more]', gridBox);
    var list = $('[data-cbb-grid-list]', gridBox);
    if (!box || !list || !view) return;
    var shown = gridShown(list);
    var more = gridHasMore(list);
    var h = '';
    if (!shown && !more) h += '<div class="cbb-empty">' + esc(view.emptyText || T.empty) + '</div>';
    // the button comes back only where scrolling cannot carry on: no observer, or a page that
    // failed to load (watching again would keep asking for it); a press tries once more
    var manual = more && (!gridEnd || view.failed);
    if (manual) h += '<button type="button" class="cbb-moreb" data-cbb-more>' + esc(T.showMore) + '</button>';
    if (shown && metal === 'all') {
      h += '<div class="cbb-progress">' + esc(more
        ? fill(T.showing, { shown: shown, total: view.total })
        : fill(T.showingAll, { total: shown })) + '</div>';
    }
    box.innerHTML = h;
    // observing again reports at once whether the end is already near, so a tall screen
    // keeps filling until the end is out of reach
    if (gridEnd) {
      gridEnd.disconnect();
      if (more && !manual) gridEnd.observe(box);
    }
  }

  function loadGrid() {
    if (!view || view.loading) return Promise.resolve();
    var v = view;
    v.loading = true;
    v.failed = false;
    var list = $('[data-cbb-grid-list]', gridBox);
    // more for a grid that already has cards: three dots under it while the next ones come
    // (an empty grid keeps its placeholder cards); the footer replaces them when done
    var foot = $('[data-cbb-grid-more]', gridBox);
    if (foot && gridShown(list)) foot.innerHTML = '<div class="cbb-gload" aria-hidden="true"><i></i><i></i><i></i></div>';
    var next = function () {
      if (v.items.filter(passMetal).length >= v.limit || v.page >= v.pages) return Promise.resolve();
      return getFeed(v.sh.url, v.page + 1, v.sort).then(function (d) {
        if (view !== v) return;
        v.page = d.page || v.page + 1;
        v.pages = d.pages || v.page;
        // paginate counts sold out products too, so take off the ones the feed left behind
        v.dropped += d.dropped || 0;
        v.total = Math.max(0, (d.total || v.total) - v.dropped);
        var fresh = (d.items || []).filter(function (p) { return p.av && !p.skip && !v.ids.has(p.id); });
        fresh.forEach(function (p) { v.ids.add(p.id); v.items.push(p); });
        gridDeal(list);
        return next();
      });
    };
    return next().catch(function (e) {
      v.failed = true;
      console.warn('[Charms bracelet builder] grid', e);
    }).then(function () {
      if (view !== v) return;
      v.loading = false;
      gridDeal(list);
      list.querySelectorAll('.cbb-skel').forEach(function (x) { x.remove(); });
      gridFooter();
    });
  }

  function regrid() {
    var list = $('[data-cbb-grid-list]', gridBox);
    if (!view || !list) return;
    var vis = view.items.filter(passMetal);
    list.innerHTML = vis.slice(0, view.limit).map(cardHtml).join('');
    if (vis.length < view.limit && view.page < view.pages) loadGrid();
    else gridFooter();
  }

  function showOverview() {
    if (!view) return;
    view = null;
    if (gridEnd) gridEnd.disconnect();
    gridBox.hidden = true;
    gridBox.innerHTML = '';
    overview.hidden = false;
    markTheme('all');
    scrollTo({ top: overviewY, behavior: 'auto' });
    // back on the shelves, the address loses the shelf's link
    var href = shelfHref(null);
    if (href && href !== location.pathname + location.search + location.hash) {
      try { history.replaceState(history.state, '', href); } catch (e) { /* history unavailable */ }
    }
  }

  /* Reached with a shelf's link, the page opens that shelf. The landing entry becomes
     the shelves, so the Back button returns to them instead of leaving the site. A
     link to a shelf that is not on the page opens the page as usual. */
  function openFromLink() {
    var key = null;
    try { key = new URL(location.href).searchParams.get(SHELF_PARAM); } catch (e) { return; }
    var sh = shelfFromKey(key);
    if (!sh) return;
    var st = null;
    try { st = history.state; } catch (e) { /* ignore */ }
    // a reload of an open shelf keeps the entries it already has
    if (st && st.cbbTheme === sh.id) { openTheme(sh.id, { fromHistory: true }); return; }
    try { history.replaceState(st, '', shelfHref(null)); } catch (e) { /* history unavailable */ }
    openTheme(sh.id, { keepScroll: true });
    // the page above the shelf keeps growing while it loads (images), so the shelf is kept
    // in view until a moment after the page has loaded, unless the shopper moves first
    var moved = false;
    var mark = function () { moved = true; };
    ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach(function (ev) { addEventListener(ev, mark, { once: true, passive: true }); });
    var until = Date.now() + 10000;
    var align = function () {
      if (moved || !view || view.sh !== sh || Date.now() > until) return;
      scrollTo({ top: browse.getBoundingClientRect().top + scrollY - headerOffset() - 12, behavior: 'auto' });
    };
    var ro = window.ResizeObserver ? new ResizeObserver(align) : null;
    if (ro) ro.observe(document.body);
    var settle = function () {
      align();
      until = Math.min(until, Date.now() + 2500);
      setTimeout(function () { align(); if (ro) ro.disconnect(); }, 2500);
    };
    align();
    if (document.readyState === 'complete') settle();
    else addEventListener('load', settle, { once: true });
  }

  function goBack() {
    var st = null;
    try { st = history.state; } catch (e) { /* ignore */ }
    if (st && st.cbbTheme) history.back();
    else showOverview();
  }

  window.addEventListener('popstate', function (e) {
    var id = e.state && e.state.cbbTheme;
    if (id && shelfById.has(id)) openTheme(id, { fromHistory: true });
    else showOverview();
  });

  if (sortSel) sortSel.addEventListener('change', function () {
    if (view) openTheme(view.sh.id, { fromHistory: true, keepScroll: true });
  });

  function setMetal(m, scroll) {
    metal = m;
    $$('[data-cbb-metal]').forEach(function (b) {
      var on = b.dataset.cbbMetal === m;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    rerenderShelves();
    if (view) regrid();
    else if (bv) browseRegrid();
    checkNudge();
    if (scroll) {
      var seg = $('.cbb-seg');
      scrollTo({ top: seg.getBoundingClientRect().top + scrollY - headerOffset() - 20, behavior: 'smooth' });
    }
  }

  /* -------------------------------------------------------------- browse all */
  /* The "Browse all charms" page (page_mode "browse") lists every charm of the
     collection in a random order. The feed's pages are read in a random order and their
     charms dealt out shuffled, so the first screen is already a mix of the whole
     collection, and more pages are read only as the shopper asks for more. Shuffle deals
     everything read so far again, with one more unread page joining in each time. */
  var BROWSE = CFG.mode === 'browse';
  var BROWSE_STEP = 24;
  var bv = null;

  function shuffle(list) {
    var a = list.slice();
    for (var i = a.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var x = a[i];
      a[i] = a[j];
      a[j] = x;
    }
    return a;
  }

  function browseTake(d) {
    var fresh = (d.items || []).filter(function (p) { return p.av && !p.skip && !bv.ids.has(p.id); });
    fresh.forEach(function (p) { bv.ids.add(p.id); });
    bv.pool = shuffle(bv.pool.concat(fresh));
  }

  // reads one more page, the next of the shuffled page list, into the pool
  function browseFetch() {
    if (!bv.todo.length) return Promise.resolve();
    var page = bv.todo.shift();
    return getFeed(CFG.browseUrl, page, '').then(browseTake, function (e) {
      console.warn('[Charms bracelet builder] browse page', page, e);
    });
  }

  function browseVisible() { return bv.pool.filter(passMetal).length; }

  // tops the pool up to what the next screen needs, two random pages at a time
  function browseFill(need) {
    if (browseVisible() >= need || !bv.todo.length) return Promise.resolve();
    return Promise.all([browseFetch(), browseFetch()]).then(function () { return browseFill(need); });
  }

  function browseFooter() {
    var box = $('[data-cbb-grid-more]', gridBox);
    if (!box) return;
    var more = browseVisible() > 0 || bv.todo.length > 0;
    var h = '';
    if (!bv.shown.filter(passMetal).length && !more) h += '<div class="cbb-empty">' + esc(T.empty) + '</div>';
    if (more) h += '<button type="button" class="cbb-moreb" data-cbb-more>' + esc(T.showMore) + '</button>';
    box.innerHTML = h;
  }

  // deals the next screen of charms out of the pool, in the pool's random order
  function browseMore() {
    if (!bv || bv.loading) return Promise.resolve();
    bv.loading = true;
    var list = $('[data-cbb-grid-list]', gridBox);
    return browseFill(BROWSE_STEP).then(function () {
      var take = [];
      var keep = [];
      bv.pool.forEach(function (p) { (take.length < BROWSE_STEP && passMetal(p) ? take : keep).push(p); });
      bv.pool = keep;
      bv.shown = bv.shown.concat(take);
      list.querySelectorAll('.cbb-skel').forEach(function (x) { x.remove(); });
      if (take.length) list.insertAdjacentHTML('beforeend', take.map(cardHtml).join(''));
    }).catch(function (e) {
      console.warn('[Charms bracelet builder] browse', e);
    }).then(function () {
      bv.loading = false;
      browseFooter();
      // the metal switch was used while this screen was loading
      if (bv.redo) { bv.redo = false; browseRegrid(); }
    });
  }

  // the metal switch deals a fresh screen: keeping the cards already shown up front would
  // put the same silver cards back under All, and the switch would seem to do nothing
  function browseRegrid() {
    if (bv.loading) { bv.redo = true; return; }
    bv.pool = shuffle(bv.shown.concat(bv.pool));
    bv.shown = [];
    $('[data-cbb-grid-list]', gridBox).innerHTML = SKEL + SKEL + SKEL + SKEL;
    browseMore();
  }

  function browseShuffle(btn) {
    if (!bv || bv.loading) return;
    bv.loading = true;
    if (btn) btn.classList.add('is-busy');
    browseFetch().then(function () {
      bv.pool = shuffle(bv.pool.concat(bv.shown));
      bv.shown = [];
      bv.loading = false;
      $('[data-cbb-grid-list]', gridBox).innerHTML = SKEL + SKEL + SKEL + SKEL;
      scrollTo({ top: browse.getBoundingClientRect().top + scrollY - headerOffset() - 120, behavior: 'smooth' });
      return browseMore();
    }).then(function () {
      if (btn) btn.classList.remove('is-busy');
    });
  }

  function browseStart() {
    bv = { todo: [], pool: [], shown: [], ids: new Set(), loading: true };
    getFeed(CFG.browseUrl, 1, '').then(function (d) {
      var rest = [];
      for (var p = 2; p <= (parseInt(d.pages, 10) || 1); p++) rest.push(p);
      bv.todo = shuffle(rest);
      browseTake(d);
      // three random pages more, so the first screen is already a mix of the whole collection
      return Promise.all([browseFetch(), browseFetch(), browseFetch()]);
    }).catch(function (e) {
      console.warn('[Charms bracelet builder] browse', e);
    }).then(function () {
      bv.loading = false;
      return browseMore();
    });
  }

  /* ----------------------------------------------------------------- bracelet */
  var dock = $('[data-cbb-dock]');
  var linksEl = $('[data-cbb-links]');
  var shownKeys = [];

  function braceletHtml(list, ghost, pop) {
    var h = '';
    // never fewer than the bracelet's own slots, and never fewer than what is in the
    // cart: past ten the band keeps growing and the row scrolls sideways
    var slots = Math.max(THRESHOLD, list.length);
    for (var k = 0; k < slots; k++) {
      var i = list[k];
      if (!i) { h += '<div class="cbb-slot"><i>' + (k + 1) + '</i></div>'; continue; }
      var attrs = i.key ? ' data-cbb-rm="' + esc(i.key) + '" aria-label="' + esc(fill(T.remove, { title: i.t })) + '"' : ' tabindex="-1" aria-hidden="true"';
      h += '<button type="button" class="cbb-slot is-on' + (k === ghost ? ' is-ghost' : '') + (k === pop ? ' is-pop' : '') + '"' + attrs + '>' +
        (i.img ? '<img src="' + esc(i.img) + '" alt="">' : '') + '</button>';
    }
    return h;
  }

  /* The slot width is measured, not expressed in CSS: the band is as wide as its
     charms, so a percentage inside it would be measured against itself. Ten slots
     fill the visible width and the eleventh onwards scroll into view. */
  function sizeSlots(wrap) {
    var w = wrap.clientWidth;
    if (!w) return;
    var slotW = (w - 6 - (THRESHOLD - 1)) / THRESHOLD;
    if (slotW > 0) linksEl.style.setProperty('--cbb-slotw', slotW.toFixed(2) + 'px');
  }

  // how far the longest pendant drops below the band, so the bar grows to fit it
  function hangDepth(list, el) {
    var slotW = (el.clientWidth - 6 - (THRESHOLD - 1)) / THRESHOLD;
    var max = 0;
    // every charm on the band, not just the first ten: the ones further along
    // scroll into view and their pendants need the same room
    list.forEach(function (i) { max = Math.max(max, ((i.ar || 1) - LINK_RATIO) * slotW); });
    return Math.ceil(max);
  }

  // The hint line sits above the checkout button and carries the "remove all" link.
  // It asks first, so a mis-tap next to Checkout cannot empty the bracelet.
  var hintEl = $('[data-cbb-hint]');
  var askTimer = null;

  function renderHint(hasCharms) {
    if (!hintEl) return;
    if (askTimer) { clearTimeout(askTimer); askTimer = null; }
    hintEl.innerHTML = '<span>' + esc(T.dockHint || '') + '</span>' +
      (hasCharms && T.clear ? '<button type="button" class="cbb-clear" data-cbb-clear>' + esc(T.clear) + '</button>' : '');
  }

  function askClear() {
    hintEl.innerHTML = '<span>' + esc(T.clearAsk) + '</span>' +
      '<span class="cbb-clear__ask">' +
        '<button type="button" class="cbb-clear cbb-clear--yes" data-cbb-clear-yes>' + esc(T.clearYes) + '</button>' +
        '<button type="button" class="cbb-clear" data-cbb-clear-no>' + esc(T.clearNo) + '</button>' +
      '</span>';
    askTimer = setTimeout(function () { renderHint(unitsList().length > 0); }, 8000);
  }

  /* Each step is drawn the same width even though the ranges behind them differ
     (ten charms, then five, then ten), so the later rewards read as close rather
     than as another ten away. Set straight to its final value: a transition only
     advances while the tab is being painted, and the bar has to be right at once. */
  var ladderEl = $('[data-cbb-ladder]');
  // free shipping also counts as reached once the cart total gets there
  function stepReached(i, n) {
    return n >= LADDER[i] || (TIER_KEYS[i] === 'shipping' && shipFreeByTotal());
  }
  function renderLadder(n) {
    if (!ladderEl || LADDER.length < 2) return;
    var segs = $$('[data-cbb-seg]', ladderEl);
    var done = 0;
    var part = 0;
    for (var i = 0; i < LADDER.length; i++) {
      if (stepReached(i, n)) { done = i + 1; continue; }
      var from = i === 0 ? 0 : LADDER[i - 1];
      var span = LADDER[i] - from;
      part = span > 0 ? Math.max(0, Math.min(1, (n - from) / span)) : 0;
      break;
    }
    var filled = (done + part) / LADDER.length;
    ladderEl.style.setProperty('--cbb-clip', ((1 - filled) * 100).toFixed(2) + '%');
    segs.forEach(function (seg, i) { seg.classList.toggle('is-done', stepReached(i, n)); });
  }

  /* ---------------------------------------------------------- celebration */
  /* A toast naming the reward, and a burst of confetti drawn on a canvas. No
     library, no image. requestAnimationFrame is right here, unlike the ladder:
     an animation genuinely should pause with the tab, and a burst meant for a
     tab nobody is looking at is skipped outright rather than played later. */
  var CHEER = CFG.cheer || {};
  var cheerEl = $('[data-cbb-cheer]');
  var fxEl = $('[data-cbb-fx]');
  var cheerTimer = null;
  var fired = {};

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function confetti(count) {
    if (!fxEl || !fxEl.getContext || document.hidden || reducedMotion()) return;
    var ctx = fxEl.getContext('2d');
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var W = window.innerWidth, H = window.innerHeight;
    fxEl.width = W * dpr; fxEl.height = H * dpr;
    fxEl.style.width = W + 'px'; fxEl.style.height = H + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    fxEl.classList.add('is-on');

    var d = dock.getBoundingClientRect();
    var ox = d.left + d.width / 2, oy = d.top + 8;
    var colors = ['#c24a66', '#e08aa0', '#f7d97a', '#1f8a5b', '#2d5bb8', '#ffffff'];
    var bits = [];
    for (var k = 0; k < count; k++) {
      var a = (-Math.PI / 2) + (Math.random() - 0.5) * 1.9;
      var sp = 6 + Math.random() * 11;
      bits.push({
        x: ox + (Math.random() - 0.5) * d.width * 0.8, y: oy,
        vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
        w: 5 + Math.random() * 5, h: 7 + Math.random() * 7,
        r: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.3,
        col: colors[(Math.random() * colors.length) | 0]
      });
    }
    var started = performance.now();
    (function frame(t) {
      var age = t - started;
      ctx.clearRect(0, 0, W, H);
      var alive = false;
      bits.forEach(function (b) {
        b.vy += 0.34; b.vx *= 0.995;
        b.x += b.vx; b.y += b.vy; b.r += b.vr;
        if (b.y >= H + 40) return;
        alive = true;
        ctx.save();
        ctx.globalAlpha = Math.max(0, 1 - age / 2100);
        ctx.translate(b.x, b.y); ctx.rotate(b.r);
        ctx.fillStyle = b.col;
        ctx.fillRect(-b.w / 2, -b.h / 2, b.w, b.h);
        ctx.restore();
      });
      if (alive && age < 2400) requestAnimationFrame(frame);
      else { ctx.clearRect(0, 0, W, H); fxEl.classList.remove('is-on'); }
    })(started);
  }

  function cheer(key) {
    var copy = CHEER[key];
    if (!cheerEl || !copy || !copy[0]) return;
    $('[data-cbb-cheer-title]', cheerEl).textContent = copy[0];
    $('[data-cbb-cheer-text]', cheerEl).textContent = copy[1] || '';
    cheerEl.classList.add('is-on');
    clearTimeout(cheerTimer);
    cheerTimer = setTimeout(function () { cheerEl.classList.remove('is-on'); }, CHEER.hold || 2000);
    confetti(key === 'watch' ? 150 : 100);
  }

  /* Fires once each time a step is crossed going up. Dropping back below it clears
     the flag, so a shopper who removes and re-adds a charm is congratulated again. */
  var cheerReady = false;
  function checkTiers(n, silent) {
    LADDER.forEach(function (at, i) {
      var key = TIER_KEYS[i];
      if (!key) return;
      var on = stepReached(i, n);
      if (on && !fired[key]) { fired[key] = true; if (!silent) cheer(key); }
      else if (!on) fired[key] = false;
    });
  }


  function renderDock(pulse) {
    var units = unitsList();
    var n = units.length;
    var lines = giftLines();
    var left = Math.max(0, THRESHOLD - n);
    var unlocked = n >= THRESHOLD;
    var msgEl = $('[data-cbb-dock-msg]');
    var canChoose = G.on && G.mode === 'popup' && !!giftBox;

    var shipLeft = SHIP_AT && !shipFreeByTotal() ? Math.max(0, SHIP_AT - n) : 0;
    if (!n) msgEl.textContent = fill(T.dockEmpty, { threshold: THRESHOLD });
    // free shipping is the first step, so it is asked for before the bracelet
    else if (shipLeft > 1 && T.dockLeftShip) msgEl.textContent = fill(T.dockLeftShip, { left: shipLeft });
    else if (shipLeft === 1 && T.dockLeftShipOne) msgEl.textContent = fill(T.dockLeftShipOne, { left: 1 });
    else if (left > 1) msgEl.textContent = fill(T.dockLeft, { left: left, threshold: THRESHOLD });
    else if (left === 1) msgEl.textContent = fill(T.dockLeftOne, { left: 1, threshold: THRESHOLD });
    else if (lines.length) {
      /* With the bracelet in the cart the line moves on to whatever is next on the
         ladder, so the dock keeps asking for the step the shopper has not reached.
         The change link stays, otherwise the colour could no longer be swapped. */
      var nextAt = 0, nextKey = '';
      for (var ti = 0; ti < LADDER.length; ti++) {
        if (n < LADDER[ti]) { nextAt = LADDER[ti]; nextKey = TIER_KEYS[ti]; break; }
      }
      var changeBtn = canChoose ? ' <button type="button" class="cbb-dock__change" data-cbb-open-gift>' + esc(T.dockChange) + '</button>' : '';
      if (nextAt && nextKey && nextKey !== 'bracelet') {
        var tmpl = nextKey === 'watch' ? T.dockLeftWatch : T.dockLeftMystery;
        msgEl.innerHTML = esc(fill(tmpl, { left: nextAt - n, threshold: nextAt })) + changeBtn;
      } else {
        msgEl.innerHTML = esc(fill(T.dockDone, { variant: lines[0].vt || '' })) + changeBtn;
      }
    } else if (canChoose) {
      msgEl.innerHTML = '<button type="button" class="cbb-dock__choose" data-cbb-open-gift>' + esc(T.dockChoose) + '</button>';
    } else msgEl.textContent = fill(T.dockDone, { variant: '' });

    $('[data-cbb-dock-n]').textContent = Math.min(n, TOP) + '/' + TOP;
    renderLadder(n);
    /* Once the watch is earned but not yet chosen, checkout steps aside so the
       shopper claims it first. Nothing is picked for them. */
    var claimBtn = $('[data-cbb-open-watch]', dock);
    var payBtn = $('[data-cbb-checkout]', dock);
    if (claimBtn && payBtn) {
      var needWatch = watchDue();
      claimBtn.hidden = !needWatch;
      payBtn.hidden = needWatch;
    }
    // the first paint only records which steps are already won: arriving with a full
    // cart should not set off a celebration for something unlocked on an earlier visit
    checkTiers(n, !cheerReady);
    cheerReady = true;
    dock.classList.toggle('is-unlocked', unlocked);
    dock.classList.toggle('has-items', cart.count > 0);

    var shown = units.slice(0, TOP);
    var pop = -1;
    if (pulse) {
      for (var k = 0; k < shown.length; k++) { if (shownKeys[k] !== shown[k].key) { pop = k; break; } }
    }
    linksEl.innerHTML = braceletHtml(shown, -1, pop);
    // the wrapper carries the pendants' room, and its width is the visible one
    var wrap = $('[data-cbb-linkswrap]');
    if (wrap) {
      sizeSlots(wrap);
      wrap.style.paddingBottom = hangDepth(shown, wrap) + 'px';
      // a charm added past the tenth lands off to the right, so bring the end of
      // the band into view: otherwise the shopper sees nothing happen
      if (pulse && wrap.scrollWidth > wrap.clientWidth) wrap.scrollLeft = wrap.scrollWidth;
    }
    shownKeys = shown.map(function (u) { return u.key; });

    var more = n - TOP;
    var extra = $('[data-cbb-extra]');
    extra.classList.toggle('is-show', more > 0);
    extra.textContent = more > 1 ? fill(T.dockExtra, { n: more }) : more === 1 ? fill(T.dockExtraOne, { n: 1 }) : '';

    renderHint(n > 0);
    $('[data-cbb-items]').textContent = fill(cart.count === 1 ? T.itemsOne : T.itemsOther, { count: cart.count });
    $('[data-cbb-total]').textContent = cart.totalf || '';
    if (!cart.count) setMini(false);
    if (pulse) { dock.classList.remove('is-pulse'); void dock.offsetWidth; dock.classList.add('is-pulse'); }
    updateDock();
  }

  function setMini(on) {
    if (on && !dock.classList.contains('has-items')) return;
    dock.classList.toggle('is-mini', on);
    var g = $('[data-cbb-grab]');
    if (g) g.setAttribute('aria-label', on ? T.expand : T.minimise);
    updateDock();
  }

  function updateDock() {
    var reached = browse.getBoundingClientRect().top < innerHeight * 0.7;
    var past = root.getBoundingClientRect().bottom < innerHeight - 60;
    var show = (reached || cart.count > 0) && !past;
    dock.classList.toggle('is-show', show);
    document.documentElement.style.setProperty('--cbb-dock-h', show ? dock.offsetHeight + 'px' : '0px');
    document.body.classList.toggle('cbb-dock-on', show);
    checkNudge();
  }

  (function dockDrag() {
    var y0 = null;
    dock.addEventListener('touchstart', function (e) { y0 = e.touches[0].clientY; }, { passive: true });
    dock.addEventListener('touchmove', function (e) {
      if (y0 === null) return;
      var dy = e.touches[0].clientY - y0;
      if (dock.classList.contains('has-items') && Math.abs(dy) > 8) {
        e.preventDefault();
        dock.style.transform = dy > 0 && !dock.classList.contains('is-mini') ? 'translateY(' + Math.min(dy, 80) + 'px)' : '';
      }
    }, { passive: false });
    dock.addEventListener('touchend', function (e) {
      if (y0 === null) return;
      var dy = e.changedTouches[0].clientY - y0;
      y0 = null;
      dock.style.transform = '';
      if (dy > 35) setMini(true);
      else if (dy < -35) setMini(false);
    });
  })();

  /* -------------------------------------------------------------------- nudge */
  var nudgeOff = false;
  function checkNudge() {
    var nudge = $('[data-cbb-nudge]');
    if (!nudge) return;
    var units = unitsList();
    var metals = [];
    units.forEach(function (u) { var m = metalOf(u.tagsL, u.t); if (metals.indexOf(m) < 0) metals.push(m); });
    var m = metals.length === 1 ? metals[0] : '';
    var show = !nudgeOff && metal === 'all' && units.length >= 2 && !!m && dock.classList.contains('is-show');
    if (show) {
      var label = m === 'gold' ? T.metalGold : T.metalSilver;
      $('[data-cbb-nudge-text]').textContent = fill(T.nudge, { metal: label });
      var yes = $('[data-cbb-nudge-yes]');
      yes.innerHTML = '<i class="cbb-sw cbb-sw--' + m + '"></i>' + esc(fill(T.nudgeBtn, { metal: label }));
      yes.dataset.m = m;
    }
    nudge.classList.toggle('is-on', show);
  }

  /* --------------------------------------------------------------- overlays */
  var qv = $('[data-cbb-qv-panel]'); // the cards carry data-cbb-qv too, so the panel has its own name
  var drawer = $('[data-cbb-drawer]');
  var giftBox = $('[data-cbb-gift]');
  var wsheet = $('[data-cbb-wsheet]');
  var shade = $('[data-cbb-shade]');
  var payTpl = $('[data-cbb-pay]');
  var PAY = payTpl ? payTpl.innerHTML.trim() : '';
  var qvId = null;
  var lastFocus = null;

  function open(panel) {
    if (!panel) return;
    lastFocus = document.activeElement;
    [qv, drawer, giftBox, wsheet].forEach(function (x) { if (x && x !== panel) { x.classList.remove('is-on'); if (x === giftBox) x.hidden = true; } });
    if (panel === giftBox) panel.hidden = false;
    panel.classList.add('is-on');
    shade.classList.add('is-on');
    document.documentElement.classList.add('cbb-lock');
    var f = panel.querySelector('[data-cbb-close]');
    if (f) f.focus({ preventScroll: true });
  }

  function closeOverlays(force) {
    // the popup for a reward still to choose stays until the choice is made
    if (force !== true && lockedOpen()) return;
    var wasOpen = shade.classList.contains('is-on');
    qvId = null;
    [qv, drawer, wsheet].forEach(function (x) { if (x) x.classList.remove('is-on'); });
    if (giftBox) { giftBox.classList.remove('is-on'); giftBox.hidden = true; }
    shade.classList.remove('is-on');
    document.documentElement.classList.remove('cbb-lock');
    if (wasOpen && lastFocus && lastFocus.focus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
  }

  /* --------------------------------------------------------- rewards to choose */
  /* Once earned, the bracelet colour and the watch have to be chosen before checkout.
     The popup for one still to choose cannot be dismissed; with one chosen it only
     changes it. A reward that is sold out, or could not be added, never holds checkout. */
  var addFailed = { bracelet: false, watch: false };

  function braceletDue() {
    return !!(G.on && G.vid && G.mode === 'popup' && giftBox && !addFailed.bracelet &&
      giftBox.querySelector('input:not([disabled])') &&
      unitsList().length >= THRESHOLD && !giftLines().length);
  }

  function watchDue() {
    return !!(wsheet && WATCH_AT && !addFailed.watch && unitsList().length >= WATCH_AT && !watchLines().length);
  }

  function lockedOpen() {
    return !!((giftBox && giftBox.classList.contains('is-on') && braceletDue()) ||
      (wsheet && wsheet.classList.contains('is-on') && watchDue()));
  }

  // a popup that has to be answered has no close button
  function setRequired(panel, required) {
    $$('[data-cbb-close]', panel).forEach(function (b) { b.style.display = required ? 'none' : ''; });
  }

  // reaching the watch on this page asks for it straight away, as the bracelet does
  var watchWasReached = null;
  function askWatch(opts) {
    var reached = !!WATCH_AT && unitsList().length >= WATCH_AT;
    if (opts.userAction && watchWasReached === false && reached && watchDue() && !braceletDue()) openWatch();
    watchWasReached = reached;
  }

  /* ------------------------------------------------------------------ free watch */
  var WATCH_AT = parseInt(TIERS.watch, 10) || 0;
  var watchLines = function () { return rewardLines('watch'); };

  function openWatch() {
    if (!wsheet) return;
    var cur = watchLines()[0];
    $$('[data-cbb-pick-watch]', wsheet).forEach(function (b) {
      b.classList.toggle('is-on', !!cur && +b.getAttribute('data-cbb-pick-watch') === cur.v);
    });
    var due = watchDue();
    setRequired(wsheet, due);
    open(wsheet);
    var first = due && $('[data-cbb-pick-watch]', wsheet);
    if (first) first.focus({ preventScroll: true });
  }

  // one watch at a time: picking another replaces the one already claimed
  function pickWatch(vid) {
    var step = Promise.resolve();
    watchLines().forEach(function (l) {
      if (l.v !== vid) step = step.then(function () { return setLine(l.key, 0); });
    });
    return step.then(function () {
      if (watchLines().some(function (l) { return l.v === vid; })) return null;
      return addVariant(vid, 1, { _free_gift: 'watch' });
    });
  }

  /* The watch is kept only while it is earned, and only ever one of it. Which watch
     is the shopper's choice, so none is ever added on their behalf. */
  function syncWatch() {
    if (!WATCH_AT) return Promise.resolve(false);
    var n = unitsList().length;
    var lines = watchLines();
    var step = Promise.resolve();
    var changed = false;
    if (n < WATCH_AT) {
      lines.forEach(function (l) { step = step.then(function () { changed = true; return setLine(l.key, 0); }); });
    } else {
      if (lines[0] && lines[0].q > 1) step = step.then(function () { changed = true; return setLine(lines[0].key, 1); });
      lines.slice(1).forEach(function (l) { step = step.then(function () { changed = true; return setLine(l.key, 0); }); });
    }
    return step.then(function () { return changed; });
  }

  function openGift() {
    if (!giftBox) return;
    var cur = giftLines()[0];
    if (cur) {
      var r = giftBox.querySelector('input[value="' + cur.v + '"]');
      if (r) r.checked = true;
    }
    var due = braceletDue();
    setRequired(giftBox, due);
    open(giftBox);
    if (due) giftBox.querySelector('[data-cbb-gift-add]').focus({ preventScroll: true });
  }

  if (giftBox) {
    giftBox.querySelector('[data-cbb-gift-add]').addEventListener('click', function () {
      var r = giftBox.querySelector('input:checked');
      var vid = r ? +r.value : G.vid;
      closeOverlays(true);
      run(function () {
        var step = Promise.resolve();
        giftLines().forEach(function (l) { if (l.v !== vid) step = step.then(function () { return setLine(l.key, 0); }); });
        return step.then(function () {
          if (!giftLines().some(function (l) { return l.v === vid; })) return addGift(vid);
        }).then(function () {
          renderAll(false);
          tellTheme();
          if (watchDue()) openWatch(); // the watch may be waiting as well
        }, function (err) {
          addFailed.bracelet = true;
          throw err;
        });
      });
    });
  }

  var WHY_ICONS = [
    '<path d="M12 2.8l7.8 4.5v9.4L12 21.2l-7.8-4.5V7.3z"/><path d="M12 12l7.8-4.7M12 12v9.2M12 12L4.2 7.3"/>',
    '<path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6z"/><path d="M8.8 12.2l2.2 2.2 4.3-4.6"/>',
    '<rect x="3.5" y="9" width="17" height="11" rx="1.5"/><path d="M12 9v11M3.5 13h17M12 9c-1.5-4-6-4-5.5-1 .3 1.3 3 1 5.5 1zM12 9c1.5-4 6-4 5.5-1-.3 1.3-3 1-5.5 1z"/>',
    '<path d="M12 20.5s-7.5-4.4-7.5-10A4.3 4.3 0 0 1 12 7.6a4.3 4.3 0 0 1 7.5 2.9c0 5.6-7.5 10-7.5 10z"/>'
  ];

  function openQV(id) {
    var p = products.get(+id);
    if (!p) return;
    qvId = p.id;
    renderQV();
    open(qv);
  }

  function renderQV() {
    var p = products.get(qvId);
    if (!p) return;
    var q = qtyOf(p.v);
    var units = unitsList();
    var n = units.length;
    var mine = units.slice(0, THRESHOLD);
    var preview, ghost;
    if (q) {
      preview = mine;
      ghost = -1;
      for (var k = 0; k < mine.length; k++) { if (mine[k].v === p.v) { ghost = k; break; } }
    } else {
      preview = mine.concat([{ img: p.img, ar: p.ar, t: p.t }]).slice(0, THRESHOLD);
      ghost = n < THRESHOLD ? n : -1;
    }
    var msg = q
      ? '<b>' + esc(T.onBracelet) + '</b> ' + (q > 1 ? esc(fill(T.youHave, { qty: q })) : '')
      : n >= THRESHOLD
        ? '<b>' + esc(T.unlockedBold) + '</b> ' + esc(fill(T.unlockedText, { n: n + 1 }))
        : '<b>' + esc(T.previewBold) + '</b> ' + esc(fill(T.previewText, { n: n + 1, threshold: THRESHOLD }));
    var row = function (a, b) { return b ? '<li><span>' + esc(a) + '</span>' + esc(b) + '</li>' : ''; };
    var facts = !p.charm ? row(T.availability[0], p.av ? T.availability[1] : T.soldOut) :
      row(T.material[0], T.material[1]) +
      (p.metal ? row(T.finish, p.metal === 'gold' ? T.metalGold : T.metalSilver) : '') +
      row(T.size[0], T.size[1]) +
      row(T.type[0], p.hang ? T.type[2] : T.type[1]) +
      (p.hang ? row(T.pendant[0], T.pendant[1]) : '') +
      row(T.availability[0], p.av ? T.availability[1] : T.soldOut);

    var gv = G.variants || [];
    var pick = gv.filter(function (v) { return p.metal === 'gold' ? /gold/i.test(v.t) && !/rose/i.test(v.t) : /silver/i.test(v.t); })[0] || gv[0];
    var free = G.on && p.charm ? '<div class="cbb-qvfree">' + (pick && pick.img ? '<img src="' + esc(pick.img) + '" alt="">' : '') +
      '<div><b>' + esc(fill(T.freeTitle, { threshold: THRESHOLD })) + '</b>' + esc(fill(T.freeText, { threshold: THRESHOLD })) + '</div></div>' : '';
    var why = (T.why || []).map(function (w, i) {
      if (!w[0] && !w[1]) return '';
      return '<div><svg viewBox="0 0 24 24" aria-hidden="true">' + WHY_ICONS[i % 4] + '</svg><b>' + esc(fill(w[0])) + '</b><span>' + esc(fill(w[1])) + '</span></div>';
    }).join('');

    $('[data-cbb-qv-body]', qv).innerHTML =
      '<a class="cbb-qvimg" href="' + esc(p.u) + '">' + badges(p) + (p.img ? '<img src="' + esc(p.img) + '" alt="' + esc(p.t) + '">' : '') + '</a>' +
      '<h2><a class="cbb-qvname" href="' + esc(p.u) + '">' + esc(p.t) + '</a></h2>' +
      '<div class="cbb-qvprice">' + esc(p.pf) + (p.c ? '<s>' + esc(p.cf) + '</s><span class="cbb-bdg cbb-bdg--off">-' + p.off + '%</span>' : '') + '</div>' +
      '<ul class="cbb-facts">' + facts + '</ul>' +
      (p.charm ? '<div class="cbb-qvbr"><p>' + msg + '</p><div class="cbb-links" data-cbb-qv-links>' + braceletHtml(preview, ghost, -1) + '</div></div>' : '') +
      free + (why ? '<div class="cbb-qvwhy">' + why + '</div>' : '') +
      (PAY ? '<div class="cbb-qvpay"><p><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="10.5" width="14" height="10" rx="2"/><path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5"/></svg>' + esc(T.secure) + '</p><div class="cbb-pays">' + PAY + '</div></div>' : '');

    requestAnimationFrame(function () {
      var el = $('[data-cbb-qv-links]', qv);
      if (el) el.style.marginBottom = hangDepth(preview, el) + 'px';
    });

    var foot = !p.av
      ? '<button type="button" class="cbb-atc" disabled>' + esc(T.soldOut) + '</button>'
      : q ? stepperHtml(p.id, q, 'q')
        : '<button type="button" class="cbb-atc" data-cbb-qadd="' + p.id + '">' + BAG + esc(fill(T.addPrice, { price: p.pf })) + '</button>';
    // the product-page link sits above the button: at the very bottom a phone's own toolbar hides it
    $('[data-cbb-qv-foot]', qv).innerHTML = '<a class="cbb-full" href="' + esc(p.u) + '">' + esc(T.full) + '</a>' + foot;
  }

  function renderDrawer() {
    var list = $('[data-cbb-lines]');
    if (!cart.items.length) {
      list.innerHTML = '<div class="cbb-drawer__empty">' + esc(T.drawerEmpty) + '</div>';
    } else {
      list.innerHTML = cart.items.slice().reverse().map(function (i) {
        /* The rewards carry no remove button. They are free and already earned, and a
           stray tap on one only costs the shopper something: the bracelet and the watch
           are changed through the bar, which is where they were chosen in the first place. */
        var x = i.gift ? '' : '<button type="button" class="cbb-li__x" data-cbb-rmline="' + esc(i.key) +
          '" aria-label="' + esc(fill(T.removeLine, { title: i.t })) + '">&times;</button>';
        // "Default Title" is Shopify's name for a product with no variants: never shown
        var vt = i.vt && i.vt !== 'Default Title' ? ' &middot; ' + esc(i.vt) : '';
        return '<div class="cbb-li' + (i.gift ? ' cbb-li--free' : '') + '">' + (i.img ? '<img src="' + esc(i.img) + '" alt="">' : '') +
          '<div class="cbb-li__t">' + esc(i.t) + (i.gift ? vt : '') +
          '<small>' + esc(fill(T.drawerQty, { qty: i.q })) + '</small></div>' +
          '<div class="cbb-li__p">' + (i.gift ? esc(T.free) : esc(i.lpf)) + '</div>' + x + '</div>';
      }).join('');
    }
    $('[data-cbb-drawer-total]').textContent = cart.totalf || '';
    var slot = $('[data-cbb-upsell-slot]');
    var tpl = $('[data-cbb-upsell]');
    if (slot) slot.innerHTML = tpl && unitsList().length >= THRESHOLD ? tpl.innerHTML : '';
    $$('[data-cbb-checkout]').forEach(function (b) { b.disabled = !cart.count; });
  }

  function renderAll(pulse) {
    syncCards();
    syncWatches();
    renderDock(!!pulse);
    renderDrawer();
    renderBrPick();
    if (qvId !== null) renderQV();
  }

  /* ------------------------------------------------------- the bracelet tile */
  // The bracelet is not a charm: it is not in the feed and not in the products map,
  // so it keeps its own little bit of state. The finishes come from the config, which
  // reads them off the product's variants.
  var BR = (CFG.bracelet && CFG.bracelet.variants) || [];
  var brSel = 0;
  for (var bi = 0; bi < BR.length; bi++) { if (BR[bi].av) { brSel = bi; break; } }

  function brLine(vid) { return cart.items.filter(function (i) { return i.v === vid && !i.gift; })[0]; }
  function brQty(vid) { var l = brLine(vid); return l ? l.q : 0; }

  function renderBrPick() {
    var el = $('[data-cbb-brpick]');
    if (!el || !BR.length) return;
    var b = BR[brSel];
    var q = brQty(b.id);
    var off = b.c > b.p ? Math.round((1 - b.p / b.c) * 100) : 0;
    el.innerHTML =
      '<a class="cbb-brph" href="' + esc(b.u) + '" aria-label="' + esc(b.t) + '"><img src="' + esc(b.img) + '" alt="' + esc(b.t) + '"></a>' +
      '<div class="cbb-brinfo">' +
        '<div class="cbb-brsw" role="group">' +
          BR.map(function (x, k) {
            return '<button type="button" class="cbb-sw2 cbb-sw2--' + esc(x.sw) + (k === brSel ? ' is-on' : '') + '"' +
              (brQty(x.id) ? ' data-has="1"' : '') + (x.av ? '' : ' disabled') +
              ' data-cbb-brsw="' + k + '" aria-label="' + esc(x.t) + '"></button>';
          }).join('') +
        '</div>' +
        '<div class="cbb-brnm">' + esc(b.t) + '</div>' +
        '<div class="cbb-brprice">' + esc(b.pf) + (off ? '<s>' + esc(b.cf) + '</s><i>-' + off + '%</i>' : '') + '</div>' +
      '</div>' +
      '<div class="cbb-bract">' +
        (!b.av
          ? '<button type="button" class="cbb-atc" disabled>' + esc(T.soldOut) + '</button>'
          : (q
              ? '<div class="cbb-step"><button type="button" data-cbb-brdec="' + b.id + '" aria-label="' + esc(T.removeOne) + '">&minus;</button>' +
                '<span>' + esc(fill(T.inCart, { qty: q })) + '</span>' +
                '<button type="button" data-cbb-brinc="' + b.id + '" aria-label="' + esc(T.addOne) + '">+</button></div>'
              : '<button type="button" class="cbb-atc" data-cbb-bradd="' + b.id + '">' + BAG + esc(T.add) + '</button>')) +
      '</div>';
  }

  /* --------------------------------------------------------------- actions */
  function inc(id) {
    var p = products.get(+id);
    if (!p) return;
    run(function () { return addVariant(p.v, 1).then(function () { return afterChange({ userAction: true, pulse: true }); }); });
  }

  function dec(id) {
    var p = products.get(+id);
    if (!p) return;
    run(function () {
      var line = cart.items.filter(function (i) { return i.v === p.v && !i.gift; })[0];
      if (!line) return renderAll(false);
      return setLine(line.key, line.q - 1).then(function () { return afterChange({ userAction: true }); });
    });
  }

  function removeKey(key) {
    run(function () {
      var line = cart.items.filter(function (i) { return i.key === key; })[0];
      if (!line) return renderAll(false);
      return setLine(key, line.q - 1).then(function () { return afterChange({ userAction: true }); });
    });
  }

  // the whole line at once, for the × in the cart drawer
  function removeLine(key) {
    run(function () {
      var line = cart.items.filter(function (i) { return i.key === key; })[0];
      if (!line) return renderAll(false);
      // a reward taken out by hand stays out until the count drops and is earned again
      if (isReward(line)) rememberDeclined(rewardKey(line), true);
      return setLine(key, 0).then(function () { return afterChange({ userAction: true }); });
    });
  }

  // "Remove all" empties the bracelet: every charm plus the free bracelet that hangs off it.
  // Watches, sets and anything else in the cart are left alone.
  // One /cart/update.js call does the lot: removing the lines one by one invalidates the
  // later line keys, and Shopify then answers "no valid id or line parameter" halfway through.
  function clearCharms() {
    var updates = {};
    var any = false;
    cart.items.forEach(function (i) { if (qualifies(i) || i.gift) { updates[i.v] = 0; any = true; } });
    if (!any) return renderDock(false);
    if (hintEl) hintEl.innerHTML = '<span class="cbb-working"><i></i>' + esc(T.clearBusy || '') + '</span>';
    run(function () {
      return cartPost(UPDATE_URL, { updates: updates }).then(function () { return afterChange({}); });
    });
  }

  function addFromCard(btn, id) {
    var p = products.get(+id);
    if (!p) return;
    btn.classList.add('is-done');
    btn.textContent = '✓ ' + T.added;
    run(function () {
      return addVariant(p.v, 1).then(function () { return afterChange({ userAction: true, pulse: true }); });
    }).then(function () {
      btn.classList.remove('is-done');
      btn.innerHTML = BAG + esc(T.add);
    });
  }

  function toggleFilters() {
    var layout = $('[data-cbb-layout]');
    layout.classList.toggle('is-nofilters');
    var l = $('[data-cbb-filters-label]');
    if (l) l.textContent = layout.classList.contains('is-nofilters') ? T.filtersShow : T.filtersHide;
  }

  var toastEl = null;
  var toastTimer = null;
  function toast(msg) {
    if (!msg) return;
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'cbb-toast';
      toastEl.setAttribute('role', 'status');
      root.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.classList.remove('is-on'); }, 3500);
  }

  root.addEventListener('click', function (e) {
    var t = e.target.closest('button');
    if (!t || !root.contains(t)) return;
    var d = t.dataset;
    if (d.cbbQv !== undefined) { e.preventDefault(); openQV(d.cbbQv); return; }
    if (d.cbbAdd !== undefined) { addFromCard(t, d.cbbAdd); return; }
    if (d.cbbAddv !== undefined) { addWatch(t, +d.cbbAddv); return; }
    if (d.cbbInc !== undefined || d.cbbQinc !== undefined || d.cbbQadd !== undefined) { inc(d.cbbInc || d.cbbQinc || d.cbbQadd); return; }
    if (d.cbbDec !== undefined || d.cbbQdec !== undefined) { dec(d.cbbDec || d.cbbQdec); return; }
    if (d.cbbTheme !== undefined) { e.preventDefault(); if (d.cbbTheme === 'all') goBack(); else openTheme(d.cbbTheme); return; }
    if (d.cbbBack !== undefined) { goBack(); return; }
    if (d.cbbMore !== undefined) { if (bv) browseMore(); else loadGrid(); return; }
    if (d.cbbShuffle !== undefined) { browseShuffle(t); return; }
    if (d.cbbRnav !== undefined) {
      var row = t.closest('.cbb-rowwrap').querySelector('[data-cbb-row]');
      row.scrollBy({ left: row.clientWidth * 0.8 * (+d.cbbRnav), behavior: 'smooth' });
      return;
    }
    if (d.cbbBrsw !== undefined) { brSel = +d.cbbBrsw; renderBrPick(); return; }
    if (d.cbbBradd !== undefined || d.cbbBrinc !== undefined) {
      var brAddId = +(d.cbbBradd !== undefined ? d.cbbBradd : d.cbbBrinc);
      run(function () { return addVariant(brAddId, 1).then(function () { return afterChange({ userAction: true, pulse: true }); }); });
      return;
    }
    if (d.cbbBrdec !== undefined) {
      var brLn = brLine(+d.cbbBrdec);
      if (brLn) run(function () { return setLine(brLn.key, brLn.q - 1).then(function () { return afterChange({ userAction: true }); }); });
      return;
    }
    if (d.cbbMetal !== undefined) { setMetal(d.cbbMetal); return; }
    if (d.cbbRm !== undefined) { t.classList.add('is-out'); setTimeout(function () { removeKey(d.cbbRm); }, 200); return; }
    if (d.cbbRmline !== undefined) { var li = t.closest('.cbb-li'); if (li) li.classList.add('is-out'); removeLine(d.cbbRmline); return; }
    if (d.cbbClear !== undefined) { askClear(); return; }
    if (d.cbbClearYes !== undefined) { clearCharms(); return; }
    if (d.cbbClearNo !== undefined) { renderHint(unitsList().length > 0); return; }
    if (d.cbbOpenCart !== undefined) {
      // the theme's own cart drawer, the same cart every other page opens, with the
      // reward ladder on top; this page's small drawer is only the fallback
      var themeDrawer = document.querySelector('cart-drawer');
      if (themeDrawer) {
        tellTheme();
        themeDrawer.dispatchEvent(new CustomEvent('theme:cart-drawer:show'));
        return;
      }
      renderDrawer(); open(drawer); return;
    }
    if (d.cbbOpenGift !== undefined) { openGift(); return; }
    if (d.cbbOpenWatch !== undefined) { openWatch(); return; }
    if (d.cbbPickWatch !== undefined) {
      var pickedWatch = +d.cbbPickWatch;
      closeOverlays(true);
      run(function () {
        return pickWatch(pickedWatch).then(function () { return afterChange({ pulse: true }); }, function (err) {
          addFailed.watch = true;
          throw err;
        });
      });
      return;
    }
    if (d.cbbCheckout !== undefined) { goCheckout(t); return; }
    if (d.cbbClose !== undefined) { closeOverlays(); return; }
    if (d.cbbFiltersToggle !== undefined) { toggleFilters(); return; }
    if (d.cbbNudgeYes !== undefined) { setMetal(t.dataset.m, true); return; }
    if (d.cbbNudgeNo !== undefined) { nudgeOff = true; checkNudge(); return; }
    if (d.cbbGrab !== undefined) { setMini(!dock.classList.contains('is-mini')); }
  });

  shade.addEventListener('click', closeOverlays);
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeOverlays(); });
  window.addEventListener('scroll', updateDock, { passive: true });

  // The strip slides by half the track, so the track has to be at least twice as wide as its
  // box or a gap appears at the end of each loop. Copy whole groups until it is.
  function fillStrip() {
    var strip = $('.cbb-watchtile__strip');
    if (!strip) return;
    var track = strip.querySelector('.cbb-watchtile__track');
    var groups = track ? track.querySelectorAll('.cbb-watchtile__grp') : null;
    if (!groups || !groups.length) return;
    var one = groups[0].getBoundingClientRect().width;
    var box = strip.getBoundingClientRect().width;
    if (!one || !box) return;
    var need = Math.ceil((box * 2) / one);
    if (need % 2) need++;
    need = Math.max(2, Math.min(need, 16));
    for (var i = groups.length; i < need; i++) track.appendChild(groups[i % groups.length].cloneNode(true));
  }
  fillStrip();
  window.addEventListener('resize', function () { renderDock(false); shelves.forEach(updRow); fillStrip(); });

  /* The cart drawer on this page does not open its own bracelet popup (two popups added
     the bracelet twice); it asks for this one instead, once the drawer has closed. */
  document.addEventListener('cbb:open-gift', function (e) {
    if (!giftBox) return;
    if (e.detail) e.detail.handled = true;
    setTimeout(openGift, 320);
  });

  // The shopper can also change the cart in the theme's own drawer; follow along.
  var themeTimer = null;
  document.addEventListener('theme:cart:change', function () {
    if (Date.now() < quietUntil) return;
    clearTimeout(themeTimer);
    themeTimer = setTimeout(function () {
      run(function () {
        // a reward taken out in the theme drawer stays out until it is earned again,
        // the same as one taken out in this page's own drawer
        var hadMystery = rewardLines('mystery').length > 0;
        return loadCart().then(function () {
          var mysteryAt = parseInt(TIERS.mystery, 10) || 0;
          if (hadMystery && !rewardLines('mystery').length && mysteryAt && unitsList().length >= mysteryAt) rememberDeclined('mystery', true);
          return syncGift({});
        }).then(function (a) {
          return syncMystery().then(function (b) {
            return syncWatch().then(function (c) { return a || b || c; });
          });
        }).then(function (changed) {
          renderAll(false);
          if (changed) tellTheme();
        });
      });
    }, 400);
  });

  window.addEventListener('pageshow', function (e) {
    if (e.persisted) run(function () { return loadCart().then(function () { renderAll(false); }); });
  });

  /* ------------------------------------------------------------------ reviews */
  (function reviews() {
    var box = $('[data-cbb-revs]');
    if (!box) return;
    var figs = $$('figure', box);
    var dots = $$('.cbb-dots button', box);
    if (figs.length < 2) return;
    var k = 0;
    var timer = null;
    var go = function (n) {
      figs[k].classList.remove('is-on');
      if (dots[k]) dots[k].classList.remove('is-on');
      k = (n + figs.length) % figs.length;
      figs[k].classList.add('is-on');
      if (dots[k]) dots[k].classList.add('is-on');
    };
    var start = function () { clearInterval(timer); timer = setInterval(function () { go(k + 1); }, 5000); };
    dots.forEach(function (d, i) { d.addEventListener('click', function () { go(i); start(); }); });
    var x0 = null;
    box.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; }, { passive: true });
    box.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 35) { go(k + (dx < 0 ? 1 : -1)); start(); }
      x0 = null;
    });
    start();
  })();

  /* ----------------------------------------------------- search and the speller */
  (function tools() {
    var SEARCH = CFG.search || {};
    var LETTERS = CFG.letters || {};
    var qEl = $('[data-cbb-q]');
    var clearEl = $('[data-cbb-qclear]');
    var SEARCH_ID = '__cbb-search';

    if (qEl && SEARCH.on) {
      var timer = null;
      var last = null;

      /* The results are shown through the ordinary grid. Search is registered as a
         shelf whose feed happens to be the search route, so paging, the metal switch,
         quick view and add to cart all keep working with no second code path. */
      var go = function () {
        var q = String(qEl.value || '').trim();
        if (q === last) return;
        last = q;
        if (q.length < 2) {
          if (view && view.sh && view.sh.id === SEARCH_ID) goBack();
          return;
        }
        shelfById.set(SEARCH_ID, {
          id: SEARCH_ID,
          title: fill(T.searchTitle, { q: q }),
          url: (SEARCH.url || '/search') + '?q=' + encodeURIComponent(q) + '&type=product&options%5Bprefix%5D=last',
          sort: ''
        });
        openTheme(SEARCH_ID);
        if (view) view.emptyText = fill(T.searchNone, { q: q });
      };

      qEl.addEventListener('input', function () {
        if (clearEl) clearEl.hidden = !qEl.value;
        clearTimeout(timer);
        timer = setTimeout(go, 350);
      });
      qEl.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        clearTimeout(timer);
        go();
      });
      if (clearEl) {
        clearEl.addEventListener('click', function () {
          qEl.value = '';
          clearEl.hidden = true;
          last = null;
          if (view && view.sh && view.sh.id === SEARCH_ID) goBack();
          qEl.focus();
        });
      }
    }

    var spEl = $('[data-cbb-speller]');
    if (!spEl || !LETTERS.on || !LETTERS.url) return;

    var nameEl = $('[data-cbb-name]', spEl);
    var goEl = $('[data-cbb-name-go]', spEl);
    var prevEl = $('[data-cbb-name-prev]', spEl);
    var MAX = LETTERS.max || 12;
    var bank = null;
    var loading = null;

    /* Every letter is read once, from the tag rather than the Letter Charms
       collection: that collection holds eight of the twenty six, the tag has them all.
       Sold out letters never reach here, the feed has already left them out. */
    /* The page count is taken from the first reply and then trusted. Reading it off
       every reply meant one odd page, which carries no count, ended the walk early:
       the bank kept whichever letters had arrived so far and the rest read as "no
       charm for X". A page that fails is skipped, not allowed to stop the rest. */
    function loadLetters() {
      if (bank) return Promise.resolve(bank);
      if (loading) return loading;

      var build = function (items) {
        var out = {};
        items.forEach(function (p) {
          var m = String(p.t || '').match(/\b(?:letter|number)\s+([a-z0-9])\b/i);
          if (!m) return;
          var k = m[1].toUpperCase();
          (out[k] = out[k] || []).push(p);
        });
        return out;
      };

      loading = getFeed(LETTERS.url, 1, '').then(function (first) {
        var pages = Math.min(parseInt(first.pages, 10) || 1, 20);
        var rest = [];
        for (var p = 2; p <= pages; p++) {
          rest.push(getFeed(LETTERS.url, p, '').catch(function () { return { items: [] }; }));
        }
        return Promise.all(rest).then(function (list) {
          var items = (first.items || []);
          list.forEach(function (d) { items = items.concat(d.items || []); });
          bank = build(items);
          return bank;
        });
      });
      loading.catch(function () { loading = null; });
      return loading;
    }

    // the metal switch decides which version of a letter is used, when there is a choice
    function pickFor(ch) {
      var list = (bank && bank[ch.toUpperCase()]) || [];
      if (!list.length) return null;
      if (metal === 'all') return list[0];
      var same = list.filter(function (p) { return p.metal === metal; });
      return same[0] || list[0];
    }

    function typed() {
      return String(nameEl.value || '').replace(/[^a-z0-9]/gi, '').toUpperCase().slice(0, MAX);
    }

    function preview() {
      var raw = typed();
      if (!raw) { prevEl.innerHTML = ''; goEl.disabled = false; return; }
      loadLetters().then(function () {
        var miss = [];
        var html = raw.split('').map(function (ch) {
          var p = pickFor(ch);
          if (!p || !p.img) { miss.push(ch); return '<span class="cbb-lt cbb-lt--miss">' + esc(ch) + '</span>'; }
          return '<span class="cbb-lt"><img src="' + esc(p.img) + '" alt="' + esc(ch) + '"></span>';
        }).join('');
        if (miss.length) html += '<span class="cbb-ltmsg">' + esc(fill(T.spellNone, { letters: miss.join(', ') })) + '</span>';
        prevEl.innerHTML = html;
        goEl.disabled = miss.length === raw.length;
      });
    }

    function addName() {
      var raw = typed();
      if (!raw) return;
      goEl.disabled = true;
      prevEl.innerHTML = '<span class="cbb-ltmsg">' + esc(T.spellBusy) + '</span>';
      loadLetters().then(function () {
        var items = [];
        raw.split('').forEach(function (ch) {
          var p = pickFor(ch);
          if (p) items.push({ id: p.v, quantity: 1 });
        });
        if (!items.length) { goEl.disabled = false; preview(); return; }
        // one request for the whole name, so the cart and the dock move once
        run(function () {
          return cartPost(ADD_URL, { items: items }).then(function () { return afterChange({ userAction: true, pulse: true }); });
        }).then(function () {
          nameEl.value = '';
          goEl.disabled = false;
          prevEl.innerHTML = '<span class="cbb-ltmsg">' + esc(fill(T.spellAdded, { n: items.length })) + '</span>';
        });
      });
    }

    var pt = null;
    nameEl.addEventListener('input', function () { clearTimeout(pt); pt = setTimeout(preview, 250); });
    nameEl.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      addName();
    });
    goEl.addEventListener('click', addName);
    // a letter can read gold or silver, so the preview follows the metal switch
    root.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('[data-cbb-metal]')) setTimeout(preview, 0);
    });
  })();

  /* --------------------------------------------------------------------- start */
  run(function () {
    var touched = false;
    return loadCart().then(function () {
      wasUnlocked = unitsList().length >= THRESHOLD;
      watchWasReached = !!WATCH_AT && unitsList().length >= WATCH_AT;
      return syncGift({});
    }).then(function (changed) {
      touched = !!changed;
      // the other two rewards are settled on arrival as well, otherwise a shopper who
      // comes back with fifteen charms already in the cart never gets the mystery box
      return syncMystery();
    }).then(function (changed) {
      touched = touched || !!changed;
      return syncWatch();
    }).then(function (changed) {
      touched = touched || !!changed;
      renderAll(false);
      if (touched) tellTheme();
    });
  });
  if (BROWSE) browseStart();
  else {
    try { openFromLink(); } catch (e) { console.warn('[Charms bracelet builder] shelf link', e); }
  }
  updateDock();
})();
